import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CoordPickerModal.css';

// ── Custom pin icon — avoids webpack broken default-icon paths ─────────────────
const PIN_ICON = L.divIcon({
  html: '<div class="ccp-pin">📍</div>',
  className: '',
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -44],
});

// ── Map child: captures click events ──────────────────────────────────────────
const ClickCapture = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => onMapClick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

// ── Map child: flies to a position when signalled (paste / "Go to" button) ───
const MapAutoFly = ({ lat, lng, signal }) => {
  const map = useMap();
  const prevSignal = useRef(-1);

  useEffect(() => {
    if (signal === prevSignal.current) return;
    prevSignal.current = signal;
    const latN = parseFloat(lat);
    const lngN = parseFloat(lng);
    if (!isNaN(latN) && !isNaN(lngN)) {
      map.flyTo([latN, lngN], Math.max(map.getZoom(), 10));
    }
  }, [signal]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ── Paste parser ──────────────────────────────────────────────────────────────
// Accepts: "51.0234, -98.4567"  |  "51.0234 -98.4567"  |  "51.0234,-98.4567"
// Also tolerates trailing °N / °W suffixes
function parsePaste(raw) {
  const str = raw.trim().replace(/[°NSEW]/gi, '').replace(/\s+/g, ' ');
  const m   = str.match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
  if (!m) return null;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  if (isNaN(lat) || isNaN(lng)) return null;
  if (lat < -90 || lat > 90)   return null;
  if (lng < -180 || lng > 180) return null;
  return { lat, lng };
}

// ── Default map centre (Central Canada) ───────────────────────────────────────
const CANADA_CENTER = [53.5, -98.0];

// ─────────────────────────────────────────────────────────────────────────────
/**
 * CoordPickerModal
 *
 * Props:
 *   initLat   — string | number | '' — existing latitude in form
 *   initLng   — string | number | '' — existing longitude in form
 *   onConfirm(lat: string, lng: string) — called when user confirms (empty strings = cleared)
 *   onClose() — called when modal should close (cancel / outside click)
 */
export default function CoordPickerModal({ initLat, initLng, onConfirm, onClose }) {
  const hasInit  = initLat !== '' && initLat != null && initLng !== '' && initLng != null;
  const initLatN = hasInit ? parseFloat(initLat) : null;
  const initLngN = hasInit ? parseFloat(initLng) : null;

  const [lat,       setLat]       = useState(hasInit ? String(initLatN) : '');
  const [lng,       setLng]       = useState(hasInit ? String(initLngN) : '');
  const [paste,     setPaste]     = useState('');
  const [pasteErr,  setPasteErr]  = useState('');
  const [flySignal, setFlySignal] = useState(0); // increment to trigger MapAutoFly

  const mapCenter = hasInit ? [initLatN, initLngN] : CANADA_CENTER;
  const mapZoom   = hasInit ? 12 : 4;

  // Derive marker position from current lat/lng state
  const latN     = parseFloat(lat);
  const lngN     = parseFloat(lng);
  const markerOk = lat !== '' && lng !== '' && !isNaN(latN) && !isNaN(lngN);
  const markerPos = markerOk ? [latN, lngN] : null;

  // Validation for Confirm button
  const canConfirm =
    markerOk &&
    latN >= -90 && latN <= 90 &&
    lngN >= -180 && lngN <= 180;

  // ── Three ways to update coords ───────────────────────────────────────────
  // 1. Map click
  const handleMapClick = useCallback((newLat, newLng) => {
    setLat(parseFloat(newLat.toFixed(6)).toString());
    setLng(parseFloat(newLng.toFixed(6)).toString());
    setPaste('');
    setPasteErr('');
  }, []);

  // 2. Draggable marker
  const handleMarkerDrag = useCallback((e) => {
    const { lat: newLat, lng: newLng } = e.target.getLatLng();
    setLat(parseFloat(newLat.toFixed(6)).toString());
    setLng(parseFloat(newLng.toFixed(6)).toString());
    setPaste('');
    setPasteErr('');
  }, []);

  // 3a. Paste field
  const handlePasteChange = (e) => {
    const val = e.target.value;
    setPaste(val);
    setPasteErr('');
    if (!val.trim()) return;
    const parsed = parsePaste(val);
    if (parsed) {
      setLat(parseFloat(parsed.lat.toFixed(6)).toString());
      setLng(parseFloat(parsed.lng.toFixed(6)).toString());
      setFlySignal((s) => s + 1); // fly map to pasted location
    } else if (val.length > 5) {
      setPasteErr('Use format: lat, lng — e.g. 51.0234, -98.4567');
    }
  };

  // 3b. Manual lat input
  const handleLatChange  = (e) => { setLat(e.target.value);  setPaste(''); setPasteErr(''); };
  const handleLngChange  = (e) => { setLng(e.target.value);  setPaste(''); setPasteErr(''); };

  // "Go to marker" button — flies map to manually-typed coords
  const handleGoTo = () => {
    if (canConfirm) setFlySignal((s) => s + 1);
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    onConfirm(lat, lng);
    onClose();
  };

  const handleClear = () => {
    onConfirm('', '');
    onClose();
  };

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ccp-overlay" onClick={handleOverlayClick}>
      <div className="ccp-modal" role="dialog" aria-modal="true" aria-label="Pick location on map">

        {/* ── Header ── */}
        <div className="ccp-header">
          <span className="ccp-title">📍 Pick Location</span>
          <button className="ccp-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Map ── */}
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="ccp-map"
          zoomControl
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <ClickCapture onMapClick={handleMapClick} />
          <MapAutoFly lat={lat} lng={lng} signal={flySignal} />
          {markerPos && (
            <Marker
              position={markerPos}
              icon={PIN_ICON}
              draggable
              eventHandlers={{ dragend: handleMarkerDrag }}
            />
          )}
        </MapContainer>

        {/* ── Controls ── */}
        <div className="ccp-body">

          {/* 1. Paste field */}
          <div className="ccp-section">
            <label className="ccp-label">Paste coordinates</label>
            <input
              className={`ccp-paste-input${pasteErr ? ' ccp-paste-input--err' : ''}`}
              placeholder="e.g.  51.0234, -98.4567   or   51.0234 -98.4567"
              value={paste}
              onChange={handlePasteChange}
              autoComplete="off"
            />
            {pasteErr && <div className="ccp-err-msg">{pasteErr}</div>}
          </div>

          <div className="ccp-divider">— or enter manually —</div>

          {/* 2. Manual lat / lng */}
          <div className="ccp-coords-row">
            <div className="ccp-coord-field">
              <label className="ccp-label">Latitude</label>
              <input
                type="number"
                step="any"
                min="-90"
                max="90"
                className="ccp-coord-input"
                placeholder="e.g. 51.0234"
                value={lat}
                onChange={handleLatChange}
              />
            </div>
            <div className="ccp-coord-field">
              <label className="ccp-label">Longitude</label>
              <input
                type="number"
                step="any"
                min="-180"
                max="180"
                className="ccp-coord-input"
                placeholder="e.g. -98.4567"
                value={lng}
                onChange={handleLngChange}
              />
            </div>
            <button
              className="ccp-goto-btn"
              onClick={handleGoTo}
              disabled={!canConfirm}
              title="Fly map to these coordinates"
            >
              Go to ↗
            </button>
          </div>

          {/* Position summary */}
          {markerPos && canConfirm && (
            <div className="ccp-pos-summary">
              📍 {latN.toFixed(5)}, {lngN.toFixed(5)}
            </div>
          )}

          {/* 3. Actions */}
          <div className="ccp-actions">
            <button
              className="ccp-btn ccp-btn--confirm"
              disabled={!canConfirm}
              onClick={handleConfirm}
            >
              ✓ Confirm Location
            </button>
            <button className="ccp-btn ccp-btn--clear" onClick={handleClear}>
              ✕ Clear
            </button>
            <button className="ccp-btn ccp-btn--cancel" onClick={onClose}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
